// @ts-nocheck
import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import FixedButton from '@core-ui/fixed-button';
import { DrawerSlidePosition } from '@core-ui/motion';
import { useLocalize } from '@core-ui/localization-provider';
import { Drawer } from '@core-ui/drawer';
import { BreakpointContext, XLARGE } from '@core-ui/breakpoint-provider';
import { useAppState } from '@core-ui/app-state-provider';
import { FeatureFlagsContext } from '@core-ui/feature-flags';
import { css, CSSObject, forBrands, Theme } from '@core-ui/react-stitch';
import {
  isDashboard,
  isDrawer,
  isTop,
  Position,
} from '../../collaborators/positions';
import scrollOnClose from '../../collaborators/scroll-on-close';
import FilterIcon from './components/FilterIcon';
import CloseButton from './styled/CloseButton';
import Dashboard from './styled/Dashboard';
import DashboardItem from './styled/DashboardItem';
import DashboardTitle from './styled/DashboardTitle';
import DrawerContent from './styled/DrawerContent';
import Divider from './styled/Divider';
import DrawerTrigger from './components/DrawerTrigger';
import BrStickyRailWrapper from './components/StickyRailWrapper';

import {
  DASHBOARD_FILTER,
  MOBILE_FACETS_WRAPPER_DONE_TEXT,
  FILTERS_ARIA_LABEL,
  CLOSE_ARIA_LABEL,
} from './translations';

interface MobileProps {
  children: ComponentProps[];
  newDashboard: boolean;
  isModelToggleEnabled: boolean;
  hasMarketingDataEBB?: boolean;
  appliedFacetsCount: number;
}

interface ComponentProps extends Node {
  props: {
    htmlFor?: string;
    title?: string;
    position: Position;
  };
}

const NullWrapper = ({ children }: PropsWithChildren) => <>{children}</>;

// eslint-disable-next-line complexity
const Mobile = (props: MobileProps) => {
  const {
    children,
    appliedFacetsCount,
    newDashboard = false,
    isModelToggleEnabled = false,
    hasMarketingDataEBB = false,
  } = props;

  const { brandName } = useAppState();
  const isBR = brandName === 'br' || brandName === 'brfs';
  const { enabledFeatures } = useContext(FeatureFlagsContext);
  const { greaterOrEqualTo } = useContext(BreakpointContext);
  // If rendering BR redesign, this Mobile component is rendered for desktop
  const isDesktop = greaterOrEqualTo(XLARGE);
  const isBRcolor2023FFEnabled = enabledFeatures['br-colors-2023'];
  const brMobileStyles = (): CSSObject =>
    ({
      position: 'sticky',
      top: isDesktop ? 118 : 65,
      zIndex: 100,
      padding: '0',
    } as CSSObject);

  const brDrawerStyles = (theme: Theme): CSSObject =>
    ({
      backgroundColor: isBRcolor2023FFEnabled
        ? '#F6F4EB'
        : theme.color.inverse.b1,
      maxWidth: '20.3129rem',
    } as CSSObject);

  const brFixedButtonStyles = (theme: Theme): CSSObject =>
    ({
      backgroundColor: isBRcolor2023FFEnabled ? '#000000' : theme.color.b2,
      color: isBRcolor2023FFEnabled ? '#F6F4EB' : theme.color.inverse.b1,
    } as CSSObject);

  const mobileWrapper = () =>
    hasMarketingDataEBB && isBR ? { marginTop: '-45px', zIndex: '100' } : {};

  const mobileStyles = (theme: Theme) =>
    css({
      width: '100%',
      padding: '0 1rem',
      ...(forBrands(theme, {
        br: brMobileStyles,
        brfs: brMobileStyles,
      }) as CSSObject),
    });

  const drawerBottomRef = useRef<HTMLDivElement>(null);

  const [shouldScroll, setShouldScroll] = useState(false);
  const [drawerBottomOffsetHeight, setDrawerBottomOffsetHeight] = useState(0);
  const [open, setOpen] = useState(false);

  const openDrawer = (): void => {
    setOpen(true);
    setShouldScroll(!isBR);

    setTimeout(() => {
      setDrawerBottomOffsetHeight(drawerBottomRef?.current?.offsetHeight ?? 0);
    }, 1000);
  };

  const closeDrawer = (): void => {
    setOpen(false);
    setShouldScroll(!isBR);
  };

  const { localize } = useLocalize();
  const components = React.Children.map(children, (child) => child) ?? [];
  const topComponents = components.filter(isTop);
  const dashboardComponents = components.filter(isDashboard);
  const drawerComponents = components.filter(isDrawer);
  const showFacetCountLabel =
    Number.isInteger(appliedFacetsCount) && appliedFacetsCount >= 1
      ? `(${appliedFacetsCount})`
      : '';
  const filterText = localize(DASHBOARD_FILTER);
  const triggerButtonPrefix = newDashboard ? (
    <FilterIcon />
  ) : (
    filterText?.toLowerCase()
  );
  const triggerButtonText = (
    <>
      {triggerButtonPrefix} {showFacetCountLabel}
    </>
  );

  const drawerPosition = isBR
    ? DrawerSlidePosition.left
    : DrawerSlidePosition.right;

  useEffect(() => {
    if (shouldScroll) {
      scrollOnClose(setShouldScroll);
    }
  }, [open, shouldScroll]);

  const Wrapper = isBRcolor2023FFEnabled ? BrStickyRailWrapper : NullWrapper;

  return (
    <div css={mobileStyles} style={mobileWrapper()}>
      <Wrapper
        hasMarketingDataEBB={hasMarketingDataEBB}
        stickyOffset={isDesktop ? 118 : 65}
      >
        {topComponents}
        <Dashboard newDashboard={newDashboard}>
          <DashboardItem
            isModelToggleEnabled={isModelToggleEnabled}
            newDashboard={newDashboard}
          >
            <div className="filter-container">
              {newDashboard && (
                <DashboardTitle htmlFor="filterButton" withChevron={false}>
                  {filterText}
                </DashboardTitle>
              )}
              <DrawerTrigger
                hasMarketingDataEBB={hasMarketingDataEBB}
                newDashboard={newDashboard}
                openDrawer={openDrawer}
              >
                {triggerButtonText}
              </DrawerTrigger>
              <Drawer
                aria-label={localize(FILTERS_ARIA_LABEL)}
                container=".cat-drawer"
                dialogCSS={(theme) => ({
                  width: '80%',
                  maxWidth: '20rem',
                  ...(forBrands(theme, {
                    br: () => brDrawerStyles(theme),
                    brfs: () => brDrawerStyles(theme),
                  }) as CSSObject),
                })}
                id="facets"
                isOpen={open}
                onClose={closeDrawer}
                position={drawerPosition}
              >
                <DrawerContent>
                  <CloseButton
                    ariaLabel={localize(CLOSE_ARIA_LABEL)}
                    buttonSide={isBR ? 'right' : 'left'}
                    onClick={closeDrawer}
                  />
                  <div
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      maxHeight: `calc(100% - ${drawerBottomOffsetHeight}px)`,
                    }}
                  >
                    {drawerComponents}
                  </div>
                  <div
                    ref={drawerBottomRef}
                    css={(theme) => ({
                      padding: '1rem',
                      borderTop: `1px solid ${theme.color.gray20}`,
                    })}
                  >
                    <FixedButton
                      css={(theme) => ({
                        ...(forBrands(theme, {
                          br: () => brFixedButtonStyles(theme),
                          brfs: () => brFixedButtonStyles(theme),
                        }) as CSSObject),
                      })}
                      kind="primary"
                      onClick={closeDrawer}
                    >
                      {localize(MOBILE_FACETS_WRAPPER_DONE_TEXT)}
                    </FixedButton>
                  </div>
                </DrawerContent>
              </Drawer>
            </div>
          </DashboardItem>
          {dashboardComponents.map((component, key) => (
            <React.Fragment key={key}>
              {newDashboard && isModelToggleEnabled ? <Divider /> : null}
              <DashboardItem
                isModelToggleEnabled={isModelToggleEnabled}
                newDashboard={newDashboard}
              >
                {newDashboard && (
                  <DashboardTitle
                    htmlFor={component.props?.children?.props?.htmlFor}
                  >
                    {component.props.title}
                  </DashboardTitle>
                )}
                {component}
              </DashboardItem>
            </React.Fragment>
          ))}
        </Dashboard>
      </Wrapper>
    </div>
  );
};
Mobile.defaultProps = {
  hasMarketingDataEBB: false,
};
export default Mobile;
